import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DigitalLabTeaser = () => {
  const [activeDemo, setActiveDemo] = useState('audit');
  const [auditUrl, setAuditUrl] = useState('');
  const [auditResults, setAuditResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const tools = [
    {
      id: 'audit',
      name: 'Social Media Audit',
      description: 'Comprehensive analysis of your social media presence',
      icon: 'BarChart3',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 'performance',
      name: 'Website Performance Checker',
      description: 'Analyze your website speed and optimization opportunities',
      icon: 'Zap',
      color: 'text-trust',
      bgColor: 'bg-trust/10'
    },
    {
      id: 'roi',
      name: 'ROI Calculator',
      description: 'Calculate potential returns on digital marketing investments',
      icon: 'Calculator',
      color: 'text-conversion',
      bgColor: 'bg-conversion/10'
    }
  ];

  const handleAudit = () => {
    if (!auditUrl) return;
    
    setIsAnalyzing(true);
    
    // Simulate analysis
    setTimeout(() => {
      setAuditResults({
        score: 73,
        issues: [
          'Inconsistent posting schedule detected',
          'Low engagement rate on recent posts',
          'Missing call-to-action in bio'
        ],
        recommendations: [
          'Implement content calendar for consistent posting',
          'Use more interactive content formats',
          'Optimize bio with clear value proposition'
        ]
      });
      setIsAnalyzing(false);
    }, 2000);
  };

  const renderDemo = () => {
    switch (activeDemo) {
      case 'audit':
        return (
          <div className="space-y-6">
            <div>
              <Input
                label="Enter your social media profile URL"
                type="url"
                placeholder="https://instagram.com/yourbrand"
                value={auditUrl}
                onChange={(e) => setAuditUrl(e?.target?.value)}
                className="mb-4"
              />
              <Button
                variant="default"
                onClick={handleAudit}
                loading={isAnalyzing}
                disabled={!auditUrl}
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                iconName="Search"
                iconPosition="left"
                iconSize={16}
              >
                {isAnalyzing ? 'Analyzing...' : 'Run Free Audit'}
              </Button>
            </div>
            {auditResults && (
              <div className="bg-surface rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-foreground">Audit Results</h4>
                  <div className="flex items-center space-x-2">
                    <div className={`text-2xl font-bold ${auditResults?.score >= 80 ? 'text-success' : auditResults?.score >= 60 ? 'text-warning' : 'text-error'}`}>
                      {auditResults?.score}/100
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-error mb-2">Issues Found</h5>
                    <ul className="space-y-1">
                      {auditResults?.issues?.map((issue, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="AlertCircle" size={14} className="text-error mt-0.5 flex-shrink-0" />
                          <span>{issue}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-medium text-success mb-2">Recommendations</h5>
                    <ul className="space-y-1">
                      {auditResults?.recommendations?.map((rec, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'performance':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Speed Score', value: '94/100', color: 'text-success' },
                { label: 'SEO Score', value: '87/100', color: 'text-success' },
                { label: 'Accessibility', value: '76/100', color: 'text-warning' },
                { label: 'Best Practices', value: '91/100', color: 'text-success' }
              ]?.map((metric, index) => (
                <div key={index} className="bg-surface rounded-lg p-4 text-center">
                  <div className={`text-xl font-bold ${metric?.color}`}>{metric?.value}</div>
                  <div className="text-sm text-muted-foreground">{metric?.label}</div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button variant="outline" className="border-trust text-trust hover:bg-trust hover:text-trust-foreground">
                Run Full Performance Test
              </Button>
            </div>
          </div>
        );

      case 'roi':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Investment Details</h4>
                <div className="space-y-3">
                  <Input label="Monthly Marketing Budget" type="number" placeholder="5000" />
                  <Input label="Current Monthly Revenue" type="number" placeholder="25000" />
                  <Input label="Target Growth %" type="number" placeholder="150" />
                </div>
              </div>
              
              <div className="bg-surface rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-4">Projected Results</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Projected Revenue:</span>
                    <span className="font-bold text-success">₹62,500/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ROI:</span>
                    <span className="font-bold text-conversion">750%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payback Period:</span>
                    <span className="font-bold text-trust">2.3 months</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Digital Lab - Free Tools
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Get instant insights into your digital presence with our suite of free analysis tools. No signup required.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Tool Selection */}
          <div className="space-y-4">
            {tools?.map((tool) => (
              <button
                key={tool?.id}
                onClick={() => setActiveDemo(tool?.id)}
                className={`w-full text-left p-6 rounded-xl brand-transition ${
                  activeDemo === tool?.id
                    ? 'bg-card brand-shadow border-2 border-primary'
                    : 'bg-surface hover:bg-card hover:brand-shadow'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${tool?.bgColor} flex items-center justify-center`}>
                    <Icon name={tool?.icon} size={24} className={tool?.color} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{tool?.name}</h3>
                    <p className="text-sm text-muted-foreground">{tool?.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Demo Area */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl p-8 brand-shadow">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {tools?.find(t => t?.id === activeDemo)?.name}
                </h3>
                <p className="text-muted-foreground">
                  {tools?.find(t => t?.id === activeDemo)?.description}
                </p>
              </div>

              {renderDemo()}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready for a Complete Digital Transformation?</h3>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              These free tools are just the beginning. Let's discuss how we can implement comprehensive solutions for your business.
            </p>
            <Button
              variant="secondary"
              size="lg"
              className="bg-white text-primary hover:bg-gray-100 brand-hover-lift"
              iconName="Calendar"
              iconPosition="left"
              iconSize={20}
            >
              Schedule Free Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DigitalLabTeaser;